/**
 * The expense sheet: filing one, and re-opening one to correct it.
 *
 * It collects an amount (on a pad, a digit at a time), what it was and what
 * it was for, who put the money in — one person or several — and how it is
 * split, across all five modes. What it sends is the *mode and its inputs*,
 * never the cents: the server resolves them through `src/lib/money.ts`, the
 * same module the preview above the button resolves them through, so there is
 * one implementation of largest-remainder rounding in the system and the
 * preview cannot disagree with the answer about a penny.
 *
 * **Everything that decides anything is in `src/lib/split-draft.ts`.** This
 * file holds a `useState` of an `ExpenseSheet` and renders `resolveSheet` of
 * it; the arithmetic, the validation and the re-opening are pure functions
 * that a test can run without a DOM, which is the half of a split editor that
 * would be expensive to get wrong.
 *
 * **The default path is the whole sheet.** Almost every expense anybody files
 * is "I paid, split it equally between us", and that already took no taps to
 * agree with — but it cost two fieldsets and a scroll between the amount pad
 * and the button, which is the same thing as a tap to anybody holding a phone.
 * So the payer and split editors are folded behind the one sentence that says
 * what they currently resolve to (`describeShare`), and the sheet a person
 * lands on is: the amount, what it was, what for, when, that sentence, and the
 * button. Opening them is one press of Change.
 *
 * They open on their own in the two cases where the fold would be hiding
 * something: correcting an expense that is not the default shape
 * (`isDefaultShare` says which), and a share that has stopped adding up, so a
 * disabled button never has its reason folded away.
 *
 * **Where it opens is a caller's business, and never a decision.** A new
 * sheet takes its day, its amount and its category from whoever rendered it:
 * the receipt viewer hands it the day the receipt is from and what the OCR
 * read off it, and the home screen's composer hands it the category that
 * person filed last (`src/lib/last-expense.ts`). Every one of those lands in
 * the control somebody would otherwise have filled in, and is exactly as
 * final as if they had — there is nothing here that remembers a value was
 * suggested, because there is nothing that should behave differently if it
 * was. Filing writes the group and category back down, which is what the next
 * sheet on the home screen opens on.
 *
 * **Filing and correcting behave differently on purpose.** Filing is
 * optimistic — `useFileExpense` moves the balances before the request is
 * answered — so the sheet is finished with the moment the mutation is fired,
 * because there is nothing left to wait for on the screen. Correcting is not;
 * the sheet stays open, disabled, until the answer lands, because an edit
 * unwinds one set of lines and applies another and a half-applied prediction
 * of that is a balance that is neither the old number nor the new one.
 * `src/queries.ts` says the same thing from the other end.
 */

import { useId, useMemo, useState, type FormEvent } from 'react'
import { newIdempotencyKey, type Member, type Transaction } from '../api'
import { AmountPad } from '../components/AmountPad'
import { CategoryPicker } from '../components/CategoryPicker'
import { PayerEditor } from '../components/PayerEditor'
import { ProjectPicker } from '../components/ProjectPicker'
import { SettlementPreview } from '../components/SettlementPreview'
import { SplitEditor } from '../components/SplitEditor'
import { rememberExpenseFiled } from '../last-expense'
import { formatMoney } from '../lib/format'
import { projectIdToSend, projectPicker } from '../lib/projects'
import {
  describeShare,
  expenseSheetFrom,
  isDefaultShare,
  newExpenseSheet,
  resolveSheet,
  type ExpenseSheet,
} from '../lib/split-draft'
import { useEditExpense, useFileExpense, useProjects } from '../queries'

const today = () => new Date().toISOString().slice(0, 10)

export function AddExpense({
  groupId,
  currency,
  myId,
  members,
  editing,
  occurredOn,
  category,
  fileReceiptId,
  suggestedAmountCents,
  dismissLabel = 'Cancel',
  onDismiss,
  onDone,
}: {
  groupId: string
  currency: string
  myId: string
  /** Every member the group has ever had, in the group's own order. */
  members: readonly Member[]
  /** The expense being corrected, or nothing when this is a new one. */
  editing?: Transaction
  /**
   * What day a *new* sheet opens on. The default is today, and the one caller
   * that overrides it is the receipt viewer's "make an expense from this":
   * the receipt is from lunchtime, or from three days ago when the phone was
   * last online, and a sheet that helpfully defaults to today puts every one
   * of those on the wrong day, quietly, in a field nobody re-reads.
   */
  occurredOn?: string
  /**
   * What a *new* sheet's category starts on. The home screen's composer is the
   * caller: it opens on whatever this person filed last, so that the common
   * case — the same group, the same kind of thing, a different number — is
   * the amount and nothing else. Unset it by pressing it, like any other.
   * `src/lib/last-expense.ts` holds the rule about where it comes from.
   */
  category?: string | null
  /**
   * A receipt to file onto this expense once it is written — the other half of
   * "make an expense from this receipt".
   *
   * It is passed *into the write* rather than handled in a callback here,
   * because filing is optimistic: this sheet unmounts the moment the request
   * goes out, and a callback belonging to a component that has gone is one
   * TanStack will not run. `useFileExpense` says the same thing from the other
   * end.
   */
  fileReceiptId?: string
  /**
   * What the receipt OCR read off the same receipt, offered as a starting
   * point on the amount pad — the one place that reading ever reaches an
   * expense. It only ever seeds a *new* sheet's initial state (see `sheet`
   * below): once the pad has rendered, typing over it or clearing it is
   * exactly as final as if nothing had been suggested, because this is a
   * suggestion and never a decision. `undefined` when there is nothing to
   * suggest — a free account, a PDF, a read still in flight or one that
   * failed — and the pad opens on zero exactly as it always has.
   */
  suggestedAmountCents?: number
  /**
   * What the button beside Add expense is called. "Cancel" for a sheet that
   * was opened and can be closed again; the group screen's composer is
   * never closed — it is the screen's resting state — so there it says
   * "Clear", which is what pressing it actually does there.
   */
  dismissLabel?: string
  /**
   * What the button beside Add expense *does*, when that is not the same
   * thing as finishing. It defaults to `onDone`, which is right for a sheet
   * that can be closed and for a composer whose only state is the sheet
   * itself.
   *
   * The home screen's composer is neither: it also holds which group is being
   * filed into, chosen above the sheet, and that choice is not part of what
   * "Clear" clears. A dismiss that quietly put the group back to the one this
   * screen suggested would be undoing a decision somebody made on purpose,
   * which is the one thing a control called Clear must not do.
   */
  onDismiss?: () => void
  onDone: () => void
}) {
  const current = useMemo(() => members.filter((member) => member.leftAt === null), [members])

  /**
   * Anybody the expense already names stays on the sheet even if they have
   * since left the group, because an expense they were part of has to remain
   * correctable afterwards — that is what `members.left_at` is for, and the
   * API allows exactly this.
   */
  const onSheet = useMemo(() => {
    if (!editing) return current
    const named = new Set([
      ...editing.paid.map((allocation) => allocation.userId),
      ...editing.owed.map((allocation) => allocation.userId),
    ])
    return members.filter((member) => member.leftAt === null || named.has(member.userId))
  }, [members, current, editing])

  const [sheet, setSheet] = useState<ExpenseSheet>(() =>
    editing
      ? expenseSheetFrom(editing)
      : newExpenseSheet({
          memberIds: current.map((member) => member.userId),
          myId,
          today: occurredOn ?? today(),
          amountCents: suggestedAmountCents,
          category,
        }),
  )

  /**
   * Whether the two editors are unfolded. Decided once, from the sheet this
   * mounted with — a new sheet is the default shape and starts folded, and a
   * correction to something that was not opens on what makes it different.
   * After that it is the person's, not the sheet's: unfolding them and then
   * typing the split back to equal should not fold them away under the cursor.
   */
  const [showShare, setShowShare] = useState(
    () => !isDefaultShare(sheet, myId, onSheet.map((member) => member.userId)),
  )

  /**
   * The prefix on every id in here. The group screen renders two of these at
   * once — the composer at the top and, while somebody is correcting a row,
   * one inside that row — and a `htmlFor` that matched the wrong one of them
   * would put the focus, and the screen reader's reading of it, on the other
   * sheet's box.
   */
  const uid = useId()

  const fileExpense = useFileExpense(groupId, myId)
  const editExpense = useEditExpense(groupId)

  /**
   * Which project it goes in. Held beside the sheet rather than in it: it is
   * not money, nothing in `src/lib/split-draft.ts` has anything to decide
   * about it, and a correction only sends it when it changed — see
   * `projectIdToSend`. A new sheet opens on no project.
   */
  const initialProject = editing?.projectId ?? null
  const [projectId, setProjectId] = useState<string | null>(initialProject)
  const projects = useProjects(groupId)
  const { options: projectOptions } = projectPicker(projects.data?.projects ?? [], initialProject)

  const memberOrder = onSheet.map((member) => member.userId)
  const resolved = resolveSheet(sheet, memberOrder)
  const { preview, problems } = resolved
  const draft =
    resolved.draft === null
      ? null
      : {
          ...resolved.draft,
          projectId: projectIdToSend({
            chosen: projectId,
            initial: initialProject,
            editing: editing !== undefined,
            options: projectOptions,
          }),
        }

  const saving = editExpense.isPending
  const failure = editExpense.error

  const nameOf = (userId: string) =>
    onSheet.find((member) => member.userId === userId)?.displayName ?? 'Somebody'

  /**
   * Held open regardless of what anybody pressed: once there is an amount, a
   * share that does not reconcile is the reason the button is disabled, and a
   * reason folded out of sight is a dead end. Before there is an amount every
   * split "fails" — "Put in an amount first." — which is not somebody's
   * problem yet, so the fold survives an empty sheet.
   */
  const stuck =
    sheet.amountCents > 0 && (problems.payers !== null || problems.split !== null)
  const shareOpen = showShare || stuck

  function submit(event: FormEvent) {
    event.preventDefault()
    if (draft === null || saving) return

    if (editing) {
      editExpense.mutate({ transactionId: editing.id, draft }, { onSuccess: onDone })
      return
    }

    fileExpense.mutate({
      draft,
      // In the order the group itself keeps its people — the order the odd
      // cent of an uneven split is broken by.
      members,
      fileReceiptId,
      // Minted here rather than inside the mutation: the same value names the
      // provisional row, the request, and — offline — the queued write, so
      // whichever of those the screen ends up showing is unmistakably this
      // one. See `FileExpenseArgs.idempotencyKey`.
      idempotencyKey: newIdempotencyKey(),
    })
    // What the next sheet opens on, written after the write has gone out
    // rather than before: this is a record of expenses filed, and a sheet
    // somebody abandoned half-typed is not one. See `src/lib/last-expense.ts`.
    rememberExpenseFiled({ groupId, category: draft.category })
    onDone()
  }

  const update = <K extends keyof ExpenseSheet>(key: K, value: ExpenseSheet[K]) =>
    setSheet((previous) => ({ ...previous, [key]: value }))

  return (
    <form className="form sheet" onSubmit={submit}>
      <div className="form__row">
        <span id={`${uid}-amount-label`} className="sheet__label">
          How much?
        </span>
        <AmountPad
          amountCents={sheet.amountCents}
          currency={currency}
          labelledBy={`${uid}-amount-label`}
          onChange={(cents) => update('amountCents', cents)}
        />
        {suggestedAmountCents !== undefined && sheet.amountCents === suggestedAmountCents && (
          <p className="muted note">
            Suggested from the receipt — change it if that is not right.
          </p>
        )}
      </div>

      <div className="form__row">
        <label htmlFor={`${uid}-description`}>What was it?</label>
        <input
          id={`${uid}-description`}
          value={sheet.description}
          maxLength={200}
          required
          placeholder="Cabin rental"
          onChange={(event) => update('description', event.target.value)}
        />
      </div>

      <div className="form__row">
        <span id={`${uid}-category-label`} className="sheet__label">
          What for?
        </span>
        <CategoryPicker
          value={sheet.category}
          labelledBy={`${uid}-category-label`}
          onChange={(category) => update('category', category)}
        />
        {sheet.category !== null && (
          <p className="muted note">Press it again to leave it uncategorised.</p>
        )}
      </div>

      <div className="form__row form__row--narrow">
        <label htmlFor={`${uid}-date`}>When?</label>
        <input
          id={`${uid}-date`}
          type="date"
          value={sheet.occurredOn}
          onChange={(event) => update('occurredOn', event.target.value)}
        />
      </div>

      <ProjectPicker
        id={`${uid}-project`}
        projects={projects.data?.projects ?? []}
        currentId={initialProject}
        value={projectId}
        onChange={setProjectId}
      />

      {/*
        The two editors as one sentence, and the way back to them. The sentence
        is `describeShare`, which resolves through the same `previewSplit` the
        editor's own right-hand column does, so folding them away costs the
        per-person figure only when the split is uneven — and an uneven split
        is exactly the one somebody wants the whole column for.
      */}
      <div className="form__row share">
        <span className="sheet__label">Who paid, and who owes</span>
        <p className="share__line">
          <span className="share__said">
            {describeShare({ sheet, myId, memberOrder, nameOf, currency })}
          </span>
          {/*
            No button while the share is what is stopping the expense being
            saved: there is nothing to toggle — pressing it could not fold the
            editors away — and a control that visibly does nothing is worse
            than no control. It comes back, reading "Done", the moment the
            numbers add up again.
          */}
          {!stuck && (
            <button
              type="button"
              className="button--quiet share__change"
              aria-expanded={shareOpen}
              onClick={() => setShowShare(!showShare)}
            >
              {shareOpen ? 'Done' : 'Change'}
              <span className="visually-hidden"> who paid and how it is split</span>
            </button>
          )}
        </p>
      </div>

      {shareOpen && (
        <>
          <PayerEditor
            payers={sheet.payers}
            amountCents={sheet.amountCents}
            currency={currency}
            myId={myId}
            members={onSheet}
            onChange={(next) => update('payers', next)}
          />

          <SplitEditor
            split={sheet.split}
            amountCents={sheet.amountCents}
            currency={currency}
            myId={myId}
            members={onSheet}
            onChange={(next) => update('split', next)}
          />

          <SettlementPreview sheet={sheet} currency={currency} myId={myId} members={onSheet} />
        </>
      )}

      {failure && <p className="bad">{failure.message}</p>}

      <p className="actions">
        <button type="submit" disabled={draft === null || saving}>
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Add expense'}
        </button>
        <button
          type="button"
          className="button--quiet"
          disabled={saving}
          onClick={onDismiss ?? onDone}
        >
          {dismissLabel}
        </button>
        <span className="muted note">
          {draft !== null
            ? `${formatMoney(preview.totalCents, currency)} between ${
                preview.allocations.length
              } ${preview.allocations.length === 1 ? 'person' : 'people'}.`
            : (problems.amount ??
              problems.description ??
              problems.payers ??
              problems.split ??
              '')}
        </span>
      </p>
    </form>
  )
}
